import { useState, useEffect, useRef } from "react";

import { pdfjs, Document, Page } from 'react-pdf';
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

import { getModalStyle } from '@/utils/Modal';
import { isIOS } from '@/utils/Device';

import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';

import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';

import {
    Close as CloseIcon,
    Add as PlusIcon,
    Remove as MinusIcon,
    RestartAlt as RestartIcon,
    Download as DownloadIcon
} from '@mui/icons-material';

import IframeViewer from '@/components/IframeViewer';

// embedded means with the browser native viewer
export default function PDFViewer({open, t, url, embedded, title, disclaimer, download, downloadFile, fullScreen, handleClose}){
	
	const { sxDialogFileViewer } = getModalStyle();

    const [ numPages, setNumPages ] = useState(null);
    //const [ pageNumber, setPageNumber ] = useState(1);

    const [ containerWidth, setContainerWidth ] = useState(null);
    const containerRef = useRef(null);

    const onDocumentLoadSuccess = ({ numPages }) => {
        setNumPages(numPages);
    };

    // measure container width
    const measureContainerWidth = () => {
        if (containerRef.current) {
            setContainerWidth(containerRef.current.offsetWidth);
        }
    };

    const [ scale, setScale ] = useState(1);
    const zoomIn = () => {
        setScale(prevScale => prevScale + 0.1);
    }

    const zoomOut = () => {
        setScale(prevScale => (prevScale > 0.2 ? prevScale - 0.1 : 0.2));
    }

    const resetOut = () => {
        setScale(1);
    }

    // if resizing window
    useEffect(() => {

		// if iOS device, open externally because it does not work embedded
		if(open && embedded && isIOS()){
            window.open(url, '_blank');            
            handleClose();             
            return; 
        }
        
        // add listener if modal open
        if(open){
            window.addEventListener('resize', measureContainerWidth);
        }

        // clear the listener when unmounting the compontent or closing the modal
        return () => {
            window.removeEventListener('resize', measureContainerWidth);
        };

    }, [open]);

	return (
			embedded ? 
				<IframeViewer
                    open={open}
                    url={url}
                    title={title}
                    disclaimer={disclaimer}
                    download={download}
                    downloadFile={downloadFile}
                    fullScreen={fullScreen}
                    handleClose={handleClose}
                />
            :
				<Dialog
		            container={fullScreen?.active ? fullScreen?.node?.current : null}
		            sx={sxDialogFileViewer}
		            onClose={handleClose}
		            open={open}
		            fullWidth={true}
		            maxWidth='lg'
		            TransitionProps={{
		                onEnter: measureContainerWidth,
		            }}
		        >
		        	{
		        		title && title?.length > 0 &&
						<DialogTitle className='modal-title w-full truncate'>
				            {`${title}${numPages && numPages > 1 ? ' ('+numPages+' '+t('viewer.pages')+')' : ''}`}
				        </DialogTitle>
				    }
			        <DialogContent sx={{textAlign:'center',paddingTop:1,paddingX:1,marginRight:1}}>     
			        		{
			        			disclaimer && disclaimer?.length > 0 &&
				                <div 
				                    className='app-color mb-2' 
				                    dangerouslySetInnerHTML={{__html: disclaimer}}
				                ></div>
				            }
			                <div ref={containerRef}>
			                    {
			                        containerWidth ?                            
			                            <Document
			                                file={url}
			                                onLoadSuccess={onDocumentLoadSuccess}
			                            >
			                                {
			                                    numPages && Array.from(new Array(numPages))?.map((item, index) => (
			                                        <div 
			                                            key={`page_${index + 1}`}
			                                            className='mb-8 text-center'
			                                            style={{'width':containerWidth*scale}}
			                                        >
			                                            <Page 
			                                                pageNumber={index + 1} 
			                                                width={containerWidth}
			                                                /*width={800}*/
			                                                className='border'
			                                                renderMode="canvas"
			                                                scale={scale}
			                                            />
			                                            {
			                                                numPages > 1 &&
			                                                <p className='mt-1 text-sm'>
			                                                    {t('viewer.pagenumber',{page:index+1,total:numPages})}
			                                                </p>
			                                            }
			                                        </div>
			                                    ))
			                                }
			                            </Document>
			                        :
			                            t('actions.view.disabled')
			                    }
			                </div>                    
			        </DialogContent>            
			        <div className="modal-buttons">
			            <div className='me-1'>
			                <PlusIcon className='action-button-modal text-green-800' onClick={zoomIn}/>
			            </div>
			            <div className='me-1'>
			                <MinusIcon className='action-button-modal text-green-800' onClick={zoomOut}/>
			            </div>
			            <div className='me-1'>
			                <RestartIcon className='action-button-modal text-red-600' onClick={resetOut}/>
			            </div>
			            {
			                download &&
			                <div className='me-1'>
			                    <DownloadIcon className='action-button-modal text-blue-800' onClick={downloadFile}/>
			                </div>
			            }
			            <div className=''>
			                <CloseIcon className='action-button-modal text-black' onClick={handleClose}/>
			            </div>
			        </div>
		        </Dialog>
    );
}